import { FC, Fragment } from 'react';
import { TablePagination } from '../types';
import { calculatePaginationInfo } from '../utils';
import { i18n } from '../../../translate/i18n';

interface TableFooterProps {
  pagination: TablePagination;
  onPaginationChange: (pagination: TablePagination) => void;
  loading?: boolean;
  color?: string;
}

export const TableFooter: FC<TableFooterProps> = ({
  pagination,
  onPaginationChange,
  loading = false,
  color = 'primary',
}) => {
  const { totalPages, startItem, endItem, hasNextPage, hasPreviousPage } = 
    calculatePaginationInfo(pagination.total, pagination.page, pagination.pageSize);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== pagination.page && !loading) {
      onPaginationChange({
        ...pagination,
        page: newPage,
      });
    }
  };

  const renderPaginationNumbers = () => {
    const pages: number[] = [];
    const maxVisible = 5;
    const currentPage = pagination.page;
    
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + maxVisible - 1);
    
    // Ajusta o início se necessário
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  if (pagination.total === 0) {
    return null;
  }

  return (
    <div className='py-3 d-flex justify-content-between text-xs h-100 w-100 text-gray-500 text-uppercase'>
      {/* Informações dos registros */}
      <span className='d-flex justify-content-between align-items-center col-6'>
        {i18n.t('general.showing') || 'Mostrando'} {i18n.t('general.of') || 'de'}{' '}
        {startItem} {i18n.t('general.to') || 'até'} {endItem}{' '}
        {i18n.t('general.of') || 'de'} {pagination.total}{' '}
        {i18n.t('general.records') || 'registros'}
      </span>

      {/* Paginação */}
      {totalPages > 1 && (
        <span className='d-flex col-4 justify-content-end'>
          <nav aria-label='Table navigation'>
            <ul className='d-flex inline-flex align-items-center m-0 list-unstyled'>
              {/* Botão anterior */}
              <li>
                {hasPreviousPage && (
                  <button
                    className={`px-3 py-1 rounded border-0 bg-transparent text-hover-${color} ${loading ? 'disabled' : ''}`}
                    aria-label='Previous'
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={loading}
                  >
                    <i className='fas fa-chevron-left ms-3'></i>
                  </button>
                )}
              </li>

              {/* Números das páginas */}
              {renderPaginationNumbers().map((pageNumber: number) => (
                <Fragment key={pageNumber}>
                  {pageNumber === pagination.page ? (
                    <button
                      className={`px-3 py-1 text-white transition-colors duration-150 bg-${color} border-0 rounded`}
                      disabled
                    >
                      {pageNumber}
                    </button>
                  ) : (
                    <button
                      onClick={() => handlePageChange(pageNumber)}
                      className={`px-3 py-1 rounded border-0 bg-transparent text-hover-${color} ${loading ? 'disabled' : ''}`}
                      disabled={loading}
                    >
                      {pageNumber}
                    </button>
                  )}
                </Fragment>
              ))}

              {/* Botão próximo */}
              <li>
                {hasNextPage && (
                  <button
                    className={`px-3 py-1 rounded border-0 bg-transparent text-hover-${color} ${loading ? 'disabled' : ''}`}
                    aria-label='Next'
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={loading}
                  >
                    <i className='fas fa-chevron-right me-3'></i>
                  </button>
                )}
              </li>
            </ul>
          </nav>
        </span>
      )}
    </div>
  );
};
